import React, { useCallback, useEffect, useState } from "react";
import { getAutorizacionRepuestos, recibirAutorizacionRepuestos } from "../api";
import { errMsg, fmtMoney, fmtDateTime } from "../utils";
import {
  StatusChip,
  vehLabel,
  money,
  bajarArchivo,
  Row,
  FacturaRows,
  Checks,
  countChecks,
} from "./RepuestosComun";
import RepuestosFacturar from "./RepuestosFacturar";

const MESES = [
  "enero", "febrero", "marzo", "abril", "mayo", "junio",
  "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre",
];

function viaLabel(v) {
  if (v === "mi_alas") return "propietario, desde Mi ALAS";
  if (v === "admin") return "oficina, desde ALAS";
  return "";
}

/** Detalle de una autorización: recibir, facturar, validación, revisión e historial. */
export default function RepuestosDetalle({ id, onBack, onToast, onChanged }) {
  const [a, setA] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState("");
  const [bajando, setBajando] = useState("");

  const load = useCallback(
    async (fallback) => {
      setError("");
      try {
        setA(await getAutorizacionRepuestos(id));
      } catch (e) {
        if (fallback) setA(fallback);
        else setError(errMsg(e, "No se pudo cargar la autorización."));
      }
    },
    [id]
  );

  useEffect(() => {
    setA(null);
    setActionError("");
    load(null);
  }, [load]);

  async function recibir() {
    setBusy(true);
    setActionError("");
    try {
      const r = await recibirAutorizacionRepuestos(a.id);
      onToast("Listo: confirmaste que recibiste la autorización.");
      onChanged();
      await load(r);
    } catch (e) {
      setActionError(errMsg(e, "No se pudo confirmar el recibido."));
    } finally {
      setBusy(false);
    }
  }

  async function facturaEnviada(r) {
    onToast("Factura enviada. Contabilidad la revisará.");
    onChanged();
    window.scrollTo(0, 0);
    await load(r);
  }

  async function bajar(tipo) {
    setBajando(tipo);
    setActionError("");
    try {
      await bajarArchivo(a, tipo);
    } catch (e) {
      setActionError(errMsg(e, "No se pudo descargar el archivo."));
    } finally {
      setBajando("");
    }
  }

  const back = (
    <button type="button" className="btn-sm ghost rep-back" onClick={onBack}>
      ← Volver a la lista
    </button>
  );

  if (error) {
    return (
      <>
        {back}
        <div className="banner err">{error}</div>
        <button className="btn" onClick={() => load(null)}>Reintentar</button>
      </>
    );
  }

  if (!a) {
    return (
      <>
        {back}
        <div className="center"><div className="spinner" /></div>
      </>
    );
  }

  // `can` viene calculado del API; si faltara, se deduce del estado.
  const can = a.can || {
    receive: a.status === "AUTORIZADA",
    invoice: a.status === "RECIBIDA" || a.status === "DEVUELTA",
  };
  const inv = a.invoice;
  const checks = a.validation?.checks || [];
  const nErr = countChecks(checks, "error");
  const nWarn = countChecks(checks, "warning");
  const review = a.review;
  const events = Array.isArray(a.events) ? a.events : [];

  return (
    <>
      {back}

      <div className="trip-head rep-head">
        <div className="rep-head-top">
          <span className="rep-head-num">{a.number || `#${a.id}`}</span>
          <StatusChip a={a} />
        </div>
        <div className="rep-head-amount">{fmtMoney(a.amount)}</div>
        <div className="meta">Valor autorizado · Bus {vehLabel(a.vehicle)}</div>
      </div>

      {actionError && <div className="banner err">{actionError}</div>}

      {a.status === "ANULADA" && (
        <div className="banner err">
          <b>Esta autorización fue anulada</b>
          {a.cancel?.at ? ` el ${fmtDateTime(a.cancel.at)}` : ""}
          {a.cancel?.by_name ? ` por ${a.cancel.by_name}` : ""}. No la factures.
          {a.cancel?.reason && <div style={{ marginTop: 6 }}>Motivo: {a.cancel.reason}</div>}
        </div>
      )}

      {a.status === "DEVUELTA" && (
        <div className="banner err">
          <b>Contabilidad te devolvió la factura.</b>
          <div style={{ marginTop: 6 }}>Motivo: {a.return_reason || "—"}</div>
          {can.invoice && (
            <div style={{ marginTop: 6 }}>Corrígela y envíala de nuevo en «Facturar de nuevo», más abajo.</div>
          )}
        </div>
      )}

      {a.status === "FACTURADA" && (
        <div className="banner info">
          Tu factura está en revisión de contabilidad. Si algo no cuadra te la devolverán con el motivo.
        </div>
      )}

      {a.status === "APROBADA" && (
        <div className="banner rep-ok">
          <b>Factura aprobada.</b> Quedó registrada y cargada al vehículo.
        </div>
      )}

      <div className="section">Datos de la autorización</div>
      <div className="card">
        <div className="v-rows rep-rows">
          <Row k="Estado">{a.status_label || a.status || "—"}</Row>
          <Row k="Fecha">{fmtDateTime(a.created_at)}</Row>
          <Row k="Bus">{vehLabel(a.vehicle)}</Row>
          <Row k="Propietario que autoriza">
            {a.owner?.name || "—"}
            {a.owner?.document_number ? ` · CC/NIT ${a.owner.document_number}` : ""}
          </Row>
          <Row k="Qué se autoriza">{a.description || "—"}</Row>
          <Row k="Valor autorizado">{money(a.amount)}</Row>
          {(a.created_by_name || a.created_via) && (
            <Row k="Creada por">
              {a.created_by_name || "—"}
              {viaLabel(a.created_via) ? ` (${viaLabel(a.created_via)})` : ""}
            </Row>
          )}
          {a.received_at && (
            <Row k="Recibida">
              {fmtDateTime(a.received_at)}
              {a.received_by_name ? ` · ${a.received_by_name}` : ""}
            </Row>
          )}
        </div>
      </div>

      {can.receive && (
        <div className="card">
          <div className="rep-card-title">¿Ya recibiste esta autorización?</div>
          <p className="rep-p">
            Revisa el bus, lo que se autoriza y el valor. Si todo está bien, confírmalo. Después podrás
            subir la factura.
          </p>
          <button type="button" className="btn accent" disabled={busy} onClick={recibir}>
            {busy ? "Confirmando…" : "Recibí la autorización"}
          </button>
        </div>
      )}

      {can.invoice && (
        <>
          <div className="section">{a.status === "DEVUELTA" ? "Facturar de nuevo" : "Facturar"}</div>
          <RepuestosFacturar key={`${a.id}-${a.status}`} a={a} onSent={facturaEnviada} />
        </>
      )}

      {inv && (
        <>
          <div className="section">
            {a.status === "DEVUELTA" ? "Factura que te devolvieron" : "Factura enviada"}
          </div>
          <div className="card">
            <FacturaRows inv={inv} saved />
            {(inv.has_xml || inv.has_pdf) && (
              <div className="rep-actions">
                {inv.has_xml && (
                  <button
                    type="button"
                    className="btn-sm ghost"
                    disabled={!!bajando}
                    onClick={() => bajar("xml")}
                  >
                    {bajando === "xml" ? "Descargando…" : "Descargar XML"}
                  </button>
                )}
                {inv.has_pdf && (
                  <button
                    type="button"
                    className="btn-sm ghost"
                    disabled={!!bajando}
                    onClick={() => bajar("pdf")}
                  >
                    {bajando === "pdf"
                      ? "Descargando…"
                      : inv.is_electronic
                      ? "Descargar PDF"
                      : "Descargar PDF / foto"}
                  </button>
                )}
              </div>
            )}
          </div>
        </>
      )}

      {a.validation && (
        <>
          <div className="section">Validación de la factura</div>
          <div className="card">
            {nErr > 0 ? (
              <div className="banner err" style={{ marginBottom: 0 }}>
                La validación encontró {nErr} {nErr === 1 ? "problema" : "problemas"} (en rojo).
              </div>
            ) : nWarn > 0 ? (
              <div className="banner warn" style={{ marginBottom: 0 }}>
                Hay {nWarn} {nWarn === 1 ? "advertencia" : "advertencias"} (en amarillo) que contabilidad
                revisará.
              </div>
            ) : (
              <div className="banner rep-ok" style={{ marginBottom: 0 }}>
                La factura pasó todas las validaciones.
              </div>
            )}
            <Checks checks={checks} />
          </div>
        </>
      )}

      {review && (
        <>
          <div className="section">Revisión de contabilidad</div>
          <div className="card">
            <div className="v-rows rep-rows">
              {(review.by_name || review.at) && (
                <Row k="Revisó">
                  {review.by_name || "—"}
                  {review.at ? ` · ${fmtDateTime(review.at)}` : ""}
                </Row>
              )}
              {review.period_year && review.period_month && (
                <Row k="Cargada al bus en">
                  {MESES[Number(review.period_month) - 1] || review.period_month} de {review.period_year}
                </Row>
              )}
              {Number(review.retefuente_amount) > 0 && (
                <Row k="Retención en la fuente">{money(review.retefuente_amount)}</Row>
              )}
              {Number(review.reteica_amount) > 0 && (
                <Row k="ReteICA">{money(review.reteica_amount)}</Row>
              )}
              {review.notes && <Row k="Nota">{review.notes}</Row>}
            </div>
          </div>
        </>
      )}

      {events.length > 0 && (
        <>
          <div className="section">Historial</div>
          <div className="card">
            <ul className="rep-timeline">
              {events.map((ev, i) => (
                <li key={`${ev.at}-${i}`} className="rep-ev">
                  <div className="rep-ev-label">{ev.label || ev.event || "—"}</div>
                  <div className="rep-ev-meta">
                    {fmtDateTime(ev.at)}
                    {ev.actor ? ` · ${ev.actor}` : ""}
                  </div>
                  {ev.detail && <div className="rep-ev-detail">{ev.detail}</div>}
                </li>
              ))}
            </ul>
          </div>
        </>
      )}
    </>
  );
}
